import React, { FC, ReactNode } from 'react';

import { Box, Button, Loader, Paper, Typography } from 'components';
import { usePlanFeature } from 'hooks';

import { useNavigate, useSelector, useTranslation } from 'third-party';

import { BILLING_ENABLED } from 'constants/common';
import { settingsTabUrl } from 'constants/url';

import { appSelectors, billingSelectors } from 'selectors';

import { PlanFeature, SettingsTab } from 'types/app';

type Props = {
  feature: PlanFeature;
  children: ReactNode;
};

export const PlanFeatureGate: FC<Props> = ({ feature, children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const planIncludes = usePlanFeature();
  const isLoaded = useSelector(billingSelectors.isEntitlementLoaded);
  const organizationId = useSelector(appSelectors.organizationId);

  if (!isLoaded) {
    return <Loader />;
  }

  if (planIncludes(feature)) {
    return <>{children}</>;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        minHeight: '24rem',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: '32rem',
          width: '100%',
          py: 6,
          px: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Typography variant="h2" mb={1}>
          {t('billing.featureLocked.header')}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          mb={BILLING_ENABLED ? 3 : 0}
        >
          {BILLING_ENABLED
            ? t('billing.featureLocked.description')
            : t('billing.featureLocked.descriptionNoBilling')}
        </Typography>
        {BILLING_ENABLED && (
          <Button
            variant="contained"
            onClick={() =>
              navigate(settingsTabUrl(organizationId, SettingsTab.Billing))
            }
            sx={{ color: 'primary.contrastText' }}
          >
            {t('billing.featureLocked.action')}
          </Button>
        )}
      </Paper>
    </Box>
  );
};
